import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../Common/Interceptors/AxiosConfig";
import { showNotification } from "../../../../Common/Toast/Toast";

const initialState = {
  errors: null,
  isLoading: false,
  deletionStatus: null, // null | 'transfer_required' | 'confirmation_needed' | 'pending_deletion'
  soleOwnedBusinesses: [],
  scheduledDeletionDate: null,
};

const AccountDeletionSlice = createSlice({
  name: "accountDeletion",
  initialState,
  reducers: {
    setErrors: (state, action) => {
      state.errors = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setDeletionStatus: (state, action) => {
      state.deletionStatus = action.payload;
    },
    setSoleOwnedBusinesses: (state, action) => {
      state.soleOwnedBusinesses = action.payload;
    },
    setScheduledDeletionDate: (state, action) => {
      state.scheduledDeletionDate = action.payload;
    },
  },
});

/**
 * Request account deletion
 * Checks for business ownership and requires transfer before deletion
 */
export const requestAccountDeletion = () => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = "users/profile/deletion/request";
    const response = await axiosInstance.post(URL);
    const { status, message, data } = response?.data || {};

    if (status === "transfer_required") {
      // User is sole owner of businesses - must transfer first
      dispatch(setDeletionStatus("transfer_required"));
      dispatch(setSoleOwnedBusinesses(data?.soleOwnedBusinesses || []));
      showNotification({
        message: message || "You must transfer ownership of your businesses before account deletion",
        theme: "light",
        type: "warning",
      });
      return { success: false, status: "transfer_required", data };
    } else if (status === "confirmation_needed") {
      // User is part of businesses but not sole owner
      dispatch(setDeletionStatus("confirmation_needed"));
      showNotification({
        message: message || "Please confirm account deletion",
        theme: "light",
        type: "warning",
      });
      return { success: false, status: "confirmation_needed", data };
    } else if (status === "success") {
      // Account deletion scheduled
      dispatch(setDeletionStatus("pending_deletion"));
      dispatch(setScheduledDeletionDate(data?.scheduledDeletionDate));
      showNotification({
        message: message || "Account deletion scheduled successfully",
        theme: "light",
        type: "success",
      });
      return { success: true, status: "pending_deletion", data };
    } else {
      const errorMsg = message || "Failed to request account deletion";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg =
      errorResponse?.data?.message ||
      error.message ||
      "Failed to request account deletion";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg,
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Get account deletion status
 */
export const getAccountDeletionStatus = () => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = "users/profile/deletion/status";
    const response = await axiosInstance.get(URL);
    const { status, data } = response?.data || {};

    if (status === "success") {
      if (data?.accountStatus === "pending_deletion") {
        dispatch(setDeletionStatus("pending_deletion"));
        dispatch(setScheduledDeletionDate(data?.scheduledDeletionDate));
      } else {
        dispatch(setDeletionStatus(null));
        dispatch(setScheduledDeletionDate(null));
      }
      return { success: true, data };
    } else {
      return { success: false };
    }
  } catch (error) {
    const errorResponse = error?.response;
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorResponse?.data?.message || error.message || "Something went wrong.",
    }));
    return { success: false };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Cancel account deletion
 */
export const cancelAccountDeletion = () => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = "users/profile/deletion/cancel";
    const response = await axiosInstance.post(URL);
    const { status, message } = response?.data || {};

    if (status === "success") {
      dispatch(setDeletionStatus(null));
      dispatch(setScheduledDeletionDate(null));
      showNotification({
        message: message || "Account deletion cancelled successfully",
        theme: "light",
        type: "success",
      });
      return { success: true, message };
    } else {
      const errorMsg = message || "Failed to cancel account deletion";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg =
      errorResponse?.data?.message ||
      error.message ||
      "Failed to cancel account deletion";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg,
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Transfer businesses before account deletion
 * @param {Array} transfers - Array of { businessId, newOwnerId }
 */
export const transferBusinessesBeforeDeletion = (transfers) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = "users/profile/deletion/transfer-businesses";
    const response = await axiosInstance.post(URL, { transfers });
    const { status, message, data } = response?.data || {};

    if (status === "success") {
      dispatch(setDeletionStatus("pending_deletion"));
      dispatch(setScheduledDeletionDate(data?.scheduledDeletionDate));
      dispatch(setSoleOwnedBusinesses([]));
      showNotification({
        message: message || "Businesses transferred and account deletion scheduled",
        theme: "light",
        type: "success",
      });
      return { success: true, data, message };
    } else {
      const errorMsg = message || "Failed to transfer businesses";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg =
      errorResponse?.data?.message ||
      error.message ||
      "Failed to transfer businesses";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg,
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

export const {
  setErrors,
  setLoading,
  setDeletionStatus,
  setSoleOwnedBusinesses,
  setScheduledDeletionDate,
} = AccountDeletionSlice.actions;

export default AccountDeletionSlice.reducer;
